import type {
  ProspectTestDrive,
  TestDriveStatus,
} from '../../../../../api/services/lupp/prospect/dto.test-drive-schedule';
import type { SalesConsultantsCalendarResponse } from '../../../../../api/services/lupp/test-drive/dto.test-drive';
import { LOCKED_TEST_DRIVE_STATUSES } from '../../../constants';

export const isTestDriveModifiable = (testDrive: ProspectTestDrive) =>
  testDrive.testDriveStatus !== 'completed' &&
  !LOCKED_TEST_DRIVE_STATUSES.includes(testDrive.testDriveStatus?.toLowerCase() ?? '');

export type SalesConsultantOption = { label: string; value: string; isAvailable: boolean };

type TimeRange = { start: string; end: string };
type Attendee = SalesConsultantsCalendarResponse['scheduleDays'][number]['attendees'][number];
type OwnBooking = TimeRange & { prospectId: string };

const overlaps = (range: TimeRange, start: string, end: string) =>
  Date.parse(start) < Date.parse(range.end) && Date.parse(range.start) < Date.parse(end);

const isSameBooking = (booking: Attendee['bookings'][number], ownBooking?: OwnBooking): boolean =>
  ownBooking !== undefined &&
  booking.prospectId === ownBooking.prospectId &&
  Date.parse(booking.startDatetime) === Date.parse(ownBooking.start) &&
  Date.parse(booking.endDatetime) === Date.parse(ownBooking.end);

// The calendar sends statuses in mixed case (e.g. "completed", "Requested").
const NON_BLOCKING_BOOKING_STATUSES = ['cancelled', 'no show'];

const isFreeForSlot = (attendee: Attendee, slot: TimeRange, ownBooking?: OwnBooking) =>
  attendee.user.availabilityStatus === 'AVAILABLE' &&
  !attendee.bookings.some(
    (booking) =>
      !NON_BLOCKING_BOOKING_STATUSES.includes(booking.bookingStatus.toLowerCase()) &&
      !isSameBooking(booking, ownBooking) &&
      overlaps(slot, booking.startDatetime, booking.endDatetime)
  ) &&
  !(attendee.exceptions ?? []).some((exception) =>
    overlaps(slot, exception.exceptionStartDatetime, exception.exceptionEndDatetime)
  );

/**
 * Every consultant on the calendar, available only when AVAILABLE with no booking or exception
 * overlapping the slot. The booking being modified doesn't block its own host; it's matched by
 * prospect and saved times since the calendar doesn't expose the test drive id.
 */
export const getSalesConsultantOptions = (
  calendar?: SalesConsultantsCalendarResponse,
  slot?: TimeRange,
  ownBooking?: OwnBooking
): SalesConsultantOption[] =>
  (calendar?.scheduleDays ?? []).flatMap((day) =>
    day.attendees.flatMap((attendee) => {
      const { userId, name, email } = attendee.user;
      if (!userId) return [];
      return [
        {
          label: name || email || userId,
          value: userId,
          isAvailable: slot ? isFreeForSlot(attendee, slot, ownBooking) : false,
        },
      ];
    })
  );

export const getHostUnavailableMessage = (labels?: Record<string, string>) =>
  labels?.['form.salesConsultantUnavailable'] ??
  'Selected Sales Consultant is not available for the selected date and time.';

/**
 * Color mapping for the test-drive status Badge. 'confirmed' and 'no show' aren't backed by TDM
 * yet (see plan's "Gaps to flag explicitly"), but the UI supports all 5 statuses now.
 */
export const getTestDriveStatusBadgeColor = (status?: string) => {
  switch ((status ?? '').toLowerCase()) {
    case 'requested':
      return 'yellow';
    case 'scheduled':
      return 'blue';
    case 'confirmed':
      return 'green';
    case 'completed':
      return 'teal';
    case 'no show':
      return 'orange';
    case 'cancelled':
      return 'gray';
    default:
      return 'gray';
  }
};

export const getTestDriveStatusLabel = (status?: string, labels?: Record<string, string>) => {
  switch ((status ?? '').toLowerCase()) {
    case 'requested':
      return labels?.['const.statusOptions.requested'] ?? 'Requested';
    case 'scheduled':
      return labels?.['const.statusOptions.scheduled'] ?? 'Scheduled';
    case 'confirmed':
      return labels?.['const.statusOptions.confirmed'] ?? 'Confirmed';
    case 'completed':
      return labels?.['const.statusOptions.completed'] ?? 'Completed';
    case 'no show':
      return labels?.['const.statusOptions.noShow'] ?? 'No Show';
    case 'cancelled':
      return labels?.['const.statusOptions.cancelled'] ?? 'Cancelled';
    default:
      return status ?? '';
  }
};

export const TEST_DRIVE_STATUS_OPTIONS: { label: string; value: TestDriveStatus }[] = [
  { label: 'Scheduled', value: 'scheduled' },
  { label: 'Confirmed', value: 'confirmed' },
  { label: 'Completed', value: 'completed' },
  { label: 'No Show', value: 'no show' },
  { label: 'Cancelled', value: 'cancelled' },
];

const CANCELLATION_REASONS = [
  {
    value: 'customer_rescheduled',
    labelKey: 'testDrive.cancellationReasons.customerRescheduled',
    fallback: 'Customer rescheduled',
  },
  {
    value: 'customer_no_longer_interested',
    labelKey: 'testDrive.cancellationReasons.customerNoLongerInterested',
    fallback: 'Customer no longer interested',
  },
  {
    value: 'vehicle_unavailable',
    labelKey: 'testDrive.cancellationReasons.vehicleUnavailable',
    fallback: 'Vehicle unavailable',
  },
  {
    value: 'duplicate_booking',
    labelKey: 'testDrive.cancellationReasons.duplicateBooking',
    fallback: 'Duplicate booking',
  },
  { value: 'other', labelKey: 'testDrive.cancellationReasons.other', fallback: 'Other' },
];

export const getCancellationReasonOptions = (labels?: Record<string, string>) =>
  CANCELLATION_REASONS.map(({ value, labelKey, fallback }) => ({
    label: labels?.[labelKey] ?? fallback,
    value,
  }));

export const getCancellationReasonLabel = (reason: string, labels?: Record<string, string>) => {
  const match = CANCELLATION_REASONS.find(({ value }) => value === reason.toLowerCase());
  return match ? (labels?.[match.labelKey] ?? match.fallback) : reason;
};

export const formatTestDriveVehicle = (make?: string, model?: string, trim?: string) => {
  return [make, model, trim].filter(Boolean).join(' ');
};
